import DraftEntityTypes from "enums/draftEntityTypes";
import { type AppDispatch, type AppThunk } from "features/Application/globaltypes/redux";
import DataService from "features/Application/services/dataServices/typedDataService";
import midnightService from "features/Application/services/midnightService/midnightService";
import {
  EmbeddedVideoDiscardSuccess,
  EmbeddedVideoEditSuccess,
  EmbeddedVideoPublishSuccess,
  LockEmbeddedVideoSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import { beginAsyncOperation } from "features/Application/slices/asyncOperationSlice";
import { type NotifyStepSettings } from "interfaces";
import { TagsEnum } from "interfaces/TagsEnum";
import { type EmbeddedVideoPayload } from "../../services/types";
import { setLoaded } from "../EmbeddedVideoDetailsSlice";
import { beginAction, createDraftSuccess, failureAction, updateBegin } from "../EmbeddedVideosEntityStateSlice";

export const lockEmbeddedVideo =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: LockEmbeddedVideoSuccess }));
    try {
      await midnightService.getEntityLock(DraftEntityTypes.EmbeddedVideos, id);
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const createEmbeddedVideo =
  (payload: EmbeddedVideoPayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    try {
      const result = await midnightService.createLock(DraftEntityTypes.EmbeddedVideos, payload);
      const entityId = result.data as number;
      dispatch(createDraftSuccess({ entityId }));
      dispatch(setLoaded());
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const updateEmbeddedVideo =
  (payload: EmbeddedVideoPayload): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(updateBegin());
    dispatch(beginAsyncOperation({ id: payload.id!, action: EmbeddedVideoEditSuccess }));
    try {
      await DataService.embeddedVideos.updateEmbeddedVideo(payload.id!, payload);
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const publishEmbeddedVideo =
  (id: number, notificationSettings?: NotifyStepSettings, notifyTypes?: number[]): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: EmbeddedVideoPublishSuccess }));
    try {
      await midnightService.releaseLock(
        DraftEntityTypes.EmbeddedVideos,
        id,
        notificationSettings ? { notificationSettings, notifyTypes } : undefined,
      );
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const discardEmbeddedVideo =
  (id: number): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(beginAction());
    dispatch(beginAsyncOperation({ id, action: EmbeddedVideoDiscardSuccess }));
    try {
      await midnightService.discardLock(DraftEntityTypes.EmbeddedVideos, id);
    } catch (error) {
      dispatch(failureAction({ error: error as Error }));
    }
  };

export const updateEmbeddedVideoTags =
  (id: number, tags: string[], type: "labels" | "softwareApplications"): AppThunk =>
  async () => {
    const tagTypeMap = {
      labels: TagsEnum.Label,
      softwareApplications: TagsEnum.SoftwareApplication,
    };

    try {
      await DataService.embeddedVideos.saveEmbeddedVideoTags(id, { tags, tagType: tagTypeMap[type] });
    } catch (error) {}
  };
